/**
 * Question World Module - QuickPracticeExam Component
 * Main exam practice page with instant feedback
 */

import React, { useEffect, useState, useMemo } from "react";
import { Typography, Row, Col, Layout, Button, Card, Progress } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";

import { getExamById } from "../../../../stateManager/reducers/studentAuthSlice";
import { Spinner } from "./components/common";
import {
    MCQ,
    MCA,
    CheckBox,
    Paragraph,
    ShortAns,
} from "./components/questions";
import { compareArray, getQuestionKey } from "./helpers";
import { instance } from "./helpers/api";

import "./styles/QuickPracticeExam.css";

const { Content } = Layout;
const { Text, Title } = Typography;

const QuickPracticeExam = (props) => {
    const { groupId, examId } = props.match.params;
    const [answeredQuestions, setAnsweredQuestions] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [userAnswers, setUserAnswers] = useState({});
    const [mode, setMode] = useState("exam"); // 'exam', 'reading', 'bookmarks'
    const [bookmarkedQuestionIds, setBookmarkedQuestionIds] = useState(new Set());
    const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);

    const dispatch = useDispatch();
    const history = useHistory();

    const examData = useSelector((state) => state.studentAuth.currentExam);

    const student = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("student") || "{}");
        } catch (error) {
            return {};
        }
    }, []);

    const studentId = student?.id || student?._id;

    useEffect(() => {
        localStorage.removeItem("localSavedAnswers");

        window.addEventListener("contextmenu", (event) => event.preventDefault());
        return () => {
            window.removeEventListener("contextmenu", (event) =>
                event.preventDefault()
            );
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            await dispatch(getExamById({ examId: examId, groupId: groupId }));
            setIsLoading(false);
        }
        fetchData();
    }, [examId, dispatch, groupId]);

    useEffect(() => {
        async function fetchBookmarks() {
            if (!studentId || !examData?.questions) return;
            setIsLoadingBookmarks(true);
            try {
                const response = await instance.get("/student-question-bookmark", {
                    params: {
                        student_id: studentId,
                    },
                });
                const bookmarks = response?.data?.data || [];
                const examQuestionIds = new Set(
                    examData.questions.map(
                        (q) => q._id || q.questionId?._id || q.question?._id
                    )
                );
                const relevantBookmarkIds = bookmarks
                    .filter((bookmark) => {
                        const qId = bookmark.question_id?._id || bookmark.question_id;
                        return examQuestionIds.has(qId);
                    })
                    .map((bookmark) => bookmark.question_id?._id || bookmark.question_id);
                setBookmarkedQuestionIds(new Set(relevantBookmarkIds));
            } catch (error) {
                console.error("Error fetching bookmarks:", error);
            } finally {
                setIsLoadingBookmarks(false);
            }
        }
        if (examData?.questions) {
            fetchBookmarks();
        }
    }, [studentId, examData?.questions]);

    const handleAnswerSelect = (questionKey, answer) => {
        setUserAnswers((prev) => ({
            ...prev,
            [questionKey]: answer,
        }));

        if (!answeredQuestions.has(questionKey)) {
            setAnsweredQuestions((prev) => new Set([...prev, questionKey]));
        }
    };

    const filteredQuestions = useMemo(() => {
        if (!examData?.questions) return [];
        if (mode === "bookmarks") {
            return examData.questions.filter((question) => {
                const questionId =
                    question._id || question.questionId?._id || question.question?._id;
                return bookmarkedQuestionIds.has(questionId);
            });
        }
        return examData.questions;
    }, [examData?.questions, mode, bookmarkedQuestionIds]);

    const getProgress = () => {
        if (!filteredQuestions || filteredQuestions.length === 0) return 0;
        let answeredInFiltered = 0;
        filteredQuestions.forEach((question, index) => {
            const questionKey = getQuestionKey(question, index);
            if (answeredQuestions.has(questionKey)) {
                answeredInFiltered++;
            }
        });
        return Math.round((answeredInFiltered / filteredQuestions.length) * 100);
    };

    // eslint-disable-next-line
    const getCorrectlyAnsweredCount = () => {
        if (!filteredQuestions || filteredQuestions.length === 0) return 0;

        let correctCount = 0;
        filteredQuestions.forEach((question, index) => {
            const questionKey = getQuestionKey(question, index);
            const userAnswer = userAnswers[questionKey];

            if (!userAnswer || !answeredQuestions.has(questionKey)) {
                return;
            }

            const correctAnswer = question.answer || [];
            const questionType = question.type;

            if (questionType === "MCQ" || questionType === "checkbox") {
                const userAnsArray = Array.isArray(userAnswer)
                    ? userAnswer
                    : [userAnswer];
                const correctAnsArray = Array.isArray(correctAnswer)
                    ? correctAnswer
                    : [correctAnswer];
                if (compareArray(userAnsArray, correctAnsArray)) {
                    correctCount++;
                }
            } else if (questionType === "MCA") {
                const userAnsArray = Array.isArray(userAnswer) ? userAnswer : [];
                const correctAnsArray = Array.isArray(correctAnswer)
                    ? correctAnswer
                    : [];
                if (userAnsArray.length === correctAnsArray.length) {
                    let allCorrect = true;
                    for (let i = 0; i < correctAnsArray.length; i++) {
                        if (userAnsArray[i] !== correctAnsArray[i]) {
                            allCorrect = false;
                            break;
                        }
                    }
                    if (allCorrect) {
                        correctCount++;
                    }
                }
            } else if (questionType === "shortAns" || questionType === "paragraph") {
                const userAnsText = (
                    Array.isArray(userAnswer) ? userAnswer[0] : userAnswer || ""
                )
                    .toString()
                    .trim()
                    .toLowerCase();
                const correctAnsText = (
                    Array.isArray(correctAnswer) ? correctAnswer[0] : correctAnswer || ""
                )
                    .toString()
                    .trim()
                    .toLowerCase();
                if (userAnsText === correctAnsText) {
                    correctCount++;
                }
            }
        });

        return correctCount;
    };

    if (isLoading || !examData || !examData.questions) {
        return <Spinner />;
    }

    const totalQuestions = filteredQuestions.length;
    const progress = getProgress();
    // const correctlyAnswered = getCorrectlyAnsweredCount();

    const renderQuestion = (question, index) => {
        const questionKey = getQuestionKey(question, index);
        const userAnswer = userAnswers[questionKey];
        const isAnswered = answeredQuestions.has(questionKey);

        const shouldShowAnswer = mode === "reading" || mode === "bookmarks";
        const showCorrectAnswer = mode === "reading" || mode === "bookmarks";

        const questionForDisplay = {
            ...question,
            questionId: {
                ...question,
                _id: question._id,
                title: question.title,
                options: question.options || [],
                answer: question.answer || [],
                explanation: question.explanation || "",
                explanationExt: question.explanationExt || [],
                image: question.image || [],
                file: question.file || [],
                optionType: question.optionType,
                type: question.type,
                status: question.status,
            },
            answer: shouldShowAnswer
                ? showCorrectAnswer
                    ? Array.isArray(question.answer)
                        ? question.answer
                        : question.answer
                            ? [question.answer]
                            : []
                    : isAnswered
                        ? Array.isArray(userAnswer)
                            ? userAnswer
                            : userAnswer
                                ? [userAnswer]
                                : []
                        : []
                : [],
            marks: question.point,
        };

        const commonProps = {
            question: questionForDisplay,
            questionIndex: index,
            examId: examId,
            groupId: groupId,
            isResult: shouldShowAnswer,
            examMeta: {
                multipleTimesSubmission: mode === "exam",
                isInstantPractice: true,
                examId: examData,
            },
            onAnswerSelect:
                mode === "exam"
                    ? (answer) => handleAnswerSelect(questionKey, answer)
                    : undefined,
            isPracticeQuiz: true,
        };

        switch (question.type) {
            case "MCQ":
                return <MCQ {...commonProps} />;
            case "MCA":
                return <MCA {...commonProps} />;
            case "checkbox":
                return <CheckBox {...commonProps} />;
            case "shortAns":
                return <ShortAns {...commonProps} />;
            case "paragraph":
                return <Paragraph {...commonProps} />;
            default:
                return null;
        }
    };

    return (
        <Layout
            className="quick_practice_exam_layout"
            style={{ minHeight: "100vh", background: "#f0f2f5" }}
        >
            <div className="quick_practice_exam_header_title">
                <Title level={6} className="quick_practice_exam_header_title_text" style={{ margin: 0 }}>
                    {examData.title || "Quick Practice Exam"}
                </Title>
            </div>

            <Content style={{ padding: "20px" }}>
                <Card
                    className="quick_practice_exam_header"
                    style={{
                        
                    }}
                >
                    <Row justify="space-between" className="quick_practice_exam_header_content" align="middle">
                        <Col>
                            <Button
                                className="quick_practice_back_button"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => history.goBack()}
                            >
                                <span className="quick_practice_back_button_text">Back</span>
                            </Button>
                        </Col>
                        <Col className="text-center">
                            {/* <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
                                {correctlyAnswered} / {totalQuestions} Correct
                            </Text> */}
                            <div style={{ display: "flex", gap: 8 }}>
                                <Button
                                    type={mode === "exam" ? "primary" : "default"}
                                    size="small"
                                    onClick={() => setMode("exam")}
                                >
                                    Exam
                                </Button>
                                <Button
                                    type={mode === "reading" ? "primary" : "default"}
                                    size="small"
                                    onClick={() => setMode("reading")}
                                >
                                    Reading
                                </Button>
                                <Button
                                    type={mode === "bookmarks" ? "primary" : "default"}
                                    size="small"
                                    onClick={() => setMode("bookmarks")}
                                    loading={isLoadingBookmarks}
                                >
                                    Bookmarks{" "}
                                    {mode === "bookmarks" && `(${filteredQuestions.length})`}
                                </Button>
                            </div>
                        </Col>
                        <Col flex="auto">
                            <div className="quick_practice_progress_container">
                                <Text strong className="quick_practice_progress_text">
                                    {(() => {
                                        let answeredInFiltered = 0;
                                        filteredQuestions.forEach((question, index) => {
                                            const questionKey = getQuestionKey(question, index);
                                            if (answeredQuestions.has(questionKey)) {
                                                answeredInFiltered++;
                                            }
                                        });
                                        return answeredInFiltered;
                                    })()}{" "}
                                    / {totalQuestions} Answered
                                </Text>

                                <div className="quick_practice_progress_bar_container">
                                    <Progress percent={progress} status="active" showInfo={true} />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>

                <div>
                    {filteredQuestions.length === 0 && mode === "bookmarks" ? (
                        <Card>
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                No bookmarked questions found in this exam.
                            </Text>
                        </Card>
                    ) : (
                        filteredQuestions.map((question, index) => {
                            const questionKey = getQuestionKey(question, index);
                            return (
                                <Card className="quick_practice_exam_question" key={questionKey} style={{ marginBottom: 20 }}>
                                    {/* <Row>
                                        <Col span={24}>
                                            <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                                                Question {index + 1} of {totalQuestions}
                                            </Text>
                                        </Col>
                                    </Row> */}
                                    <Row style={{ marginTop: 0 }}>
                                        <Col span={24}>{renderQuestion(question, index)}</Col>
                                    </Row>
                                </Card>
                            );
                        })
                    )}
                </div>
            </Content>
        </Layout>
    );
};

export default QuickPracticeExam;
